/**
 * Gmail OAuth Step
 * Step 2A: Connect Gmail via OAuth
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/base/buttons/button";
import { ArrowLeft, Shield01, Check } from "@untitledui/icons";
import { useCreateEmailAccount, useGetGmailAuthUrl } from "@/hooks/api/use-email-accounts";
import { toast } from "@/lib/toast-service";
import { EmailProvider } from "@/types/api/email";

interface GmailOAuthStepProps {
    email: string;
    displayName?: string;
    onBack: () => void;
    onSuccess: () => void;
}

export const GmailOAuthStep = ({
    email,
    displayName,
    onBack,
}: GmailOAuthStepProps) => {
    const { t } = useTranslation(["pages:email-settings"]);
    const [isConnecting, setIsConnecting] = useState(false);

    const createAccountMutation = useCreateEmailAccount();
    const getAuthUrlMutation = useGetGmailAuthUrl();

    const handleConnect = async () => {
        setIsConnecting(true);

        try {
            // Step 1: Create the email account record
            const account = await createAccountMutation.mutateAsync({
                email_address: email,
                provider: EmailProvider.GMAIL,
                display_name: displayName || undefined,
            });

            // Step 2: Get the OAuth authorization URL
            const authResponse = await getAuthUrlMutation.mutateAsync(account.account_id);

            // Step 3: Redirect to Google OAuth
            // The backend will redirect back to frontend after OAuth completion
            window.location.href = authResponse.authorization_url;
        } catch (error) {
            setIsConnecting(false);
            toast.error(t("errors.createAccount"));
        }
    };

    const permissions = [
        t("gmailOAuth.permissionRead"),
        t("gmailOAuth.permissionModify"),
    ];

    return (
        <div className="space-y-6">
            {/* Title */}
            <div>
                <h3 className="text-base font-medium text-primary">
                    {t("gmailOAuth.title")}
                </h3>
                <p className="mt-1 text-sm text-tertiary">
                    {t("gmailOAuth.description")}
                </p>
            </div>

            {/* Account Info */}
            <div className="flex items-center gap-3 rounded-lg bg-secondary p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-tertiary">
                    <Shield01 className="h-5 w-5 text-fg-brand-primary" />
                </div>
                <div>
                    <p className="font-medium text-primary">
                        {t("gmailOAuth.secureAccess")}
                    </p>
                    <p className="text-sm text-tertiary">{email}</p>
                </div>
            </div>

            {/* Permissions List */}
            <div className="space-y-3">
                <p className="text-sm font-medium text-primary">
                    {t("gmailOAuth.permissionsTitle")}
                </p>
                <ul className="space-y-2">
                    {permissions.map((permission, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-tertiary">
                            <Check className="h-4 w-4 text-fg-success-secondary" />
                            {permission}
                        </li>
                    ))}
                </ul>
            </div>

            {/* Google Sign In Button */}
            <Button
                onClick={handleConnect}
                isDisabled={isConnecting}
                isLoading={isConnecting}
                color="primary"
                size="lg"
                className="w-full"
                iconLeading={
                    !isConnecting ? (
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                            <path
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                fill="#4285F4"
                            />
                            <path
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                fill="#34A853"
                            />
                            <path
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                fill="#FBBC05"
                            />
                            <path
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                fill="#EA4335"
                            />
                        </svg>
                    ) : undefined
                }
            >
                {isConnecting ? t("gmailOAuth.connecting") : t("gmailOAuth.signInButton")}
            </Button>

            {/* Redirect Note */}
            <p className="text-center text-xs text-tertiary">
                {t("gmailOAuth.redirectNote")}
            </p>

            {/* Back Button */}
            <div className="flex justify-start pt-2">
                <Button
                    onClick={onBack}
                    isDisabled={isConnecting}
                    color="tertiary"
                    size="sm"
                    iconLeading={ArrowLeft}
                >
                    {t("actions.back")}
                </Button>
            </div>
        </div>
    );
};
